const API_BASE = '/api';

/**
 * Fetches the current authenticated user's information.
 * @returns {Promise<Object|null>} The client principal object or null if not authenticated/error.
 */
export const getUserInfo = async (userId) => {
    if (userId) {
        try {
            return await getGuestData(userId);
        } catch (error) {
            console.error('Error fetching guest info:', error);
            return null;
        }
    }

    try {
        const response = await fetch('/.auth/me');
        const payload = await response.json();
        const { clientPrincipal } = payload;

        if (!clientPrincipal) {
            return null;
        }

        // Fetch DB roles
        const rolesResponse = await fetch(`${API_BASE}/CheckRoles`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ userId: clientPrincipal.userId })
        });

        let dbRoles = [];
        if (rolesResponse.ok) {
            const rolesData = await rolesResponse.json();
            dbRoles = rolesData.roles || [];
        }

        // Security Check: clientPrincipal.userRoles should be EQUAL to dbRoles + ['anonymous', 'authenticated']
        const expectedRoles = new Set(['anonymous', 'authenticated', ...dbRoles]);
        const currentRoles = new Set(clientPrincipal.userRoles || []);

        // Find inconsistencies (difference in either direction)
        const inconsistentRoles = [
            ...[...currentRoles].filter(x => !expectedRoles.has(x)),
            ...[...expectedRoles].filter(x => !currentRoles.has(x))
        ];

        if (inconsistentRoles.length > 0) {
            console.warn('User roles in auth token do not match DB roles (strict equality enforcement). Inconsistent roles:', inconsistentRoles);
            throw new Error('INCONSISTENT_ROLES');
        }

        // Since they are equal (sets match), no need to augment, but we can ensure array format
        clientPrincipal.userRoles = Array.from(expectedRoles);

        return clientPrincipal;
    } catch (error) {
        if (error.message === 'INCONSISTENT_ROLES') {
            throw error;
        }
        console.error('Error fetching user info:', error);
        return null;
    }
};

/**
 * Fetches guests for a specific table captain.
 * @param {string} captainId - The ID of the table captain.
 * @returns {Promise<Array>} A list of guests.
 */
export const getCaptainData = async (captainId) => {
    try {
        const response = await fetch(`${API_BASE}/captainData`, {
            method: 'GET',
            headers: {
                'id': captainId,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Error fetching captain data: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching captain data:', error);
        throw error;
    }
};

export const getTableCaptainApprovals = async () => {
    const response = await fetch(`${API_BASE}/GetTableCaptainApprovals`);
    if (!response.ok) {
        throw new Error(`Failed to fetch captain approvals: ${response.statusText}`);
    }
    return response.json();
};

/**
 * Call the heartbeat endpoint.
 * @param {string} [name] - Optional name to send to the heartbeat.
 * @returns {Promise<string>} The response text.
 */
export const getHeartbeat = async (name) => {
    const url = name ? `${API_BASE}/heartbeat?name=${encodeURIComponent(name)}` : `${API_BASE}/heartbeat`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch heartbeat: ${response.statusText}`);
    }
    return await response.text();
};

/**
 * Submits a request to become a table captain.
 * @param {Object} user - The user object containing authentication details.
 * @returns {Promise<Object>} The response from the server.
 */
export const submitTableCaptainRequest = async (user) => {
    // Extract email from claims
    const emailClaim = user.claims && user.claims.find(c => c.typ === 'preferred_username');
    const email = emailClaim ? emailClaim.val : user.userDetails;

    try {
        const response = await fetch(`${API_BASE}/SubmitTableCaptainRequest`, {
            method: 'POST',
            headers: {
                'id': user.userId,
                'name': user.userDetails,
                'email': email,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                applicationResponses: user.applicationResponses
            })
        });

        if (!response.ok) {
            throw new Error(`Error submitting table captain request: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error submitting table captain request:', error);
        throw error;
    }
};

/**
 * Approves a table captain request.
 * @param {string} id - The ID of the table captain to approve.
 * @returns {Promise<Object>} The response from the server.
 */
export const approveTableCaptainRequest = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/ApproveTableCaptainRequest?id=${encodeURIComponent(id)}`, {
            method: 'POST'
        });

        if (!response.ok) {
            throw new Error(`Error approving table captain: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error approving table captain:', error);
        throw error;
    }
};

/**
 * Checks if a table captain approval request already exists for the user.
 * @param {string} userId - The ID of the user to check.
 * @returns {Promise<boolean>} True if an approval exists, false otherwise.
 */
export const checkIfTableCaptainApprovalExists = async (userId) => {
    try {
        const response = await fetch(`${API_BASE}/CheckIfTableCaptainApprovalExists`, {
            method: 'GET',
            headers: {
                'id': userId,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Error checking approval existence: ${response.statusText}`);
        }

        const data = await response.json();
        return data.exists;
    } catch (error) {
        console.error('Error checking table captain approval existence:', error);
        return false;
    }
};

/**
 * Invites a guest via email.
 * @param {string} captainId - The ID of the captain sending the invite.
 * @param {string} guestName - The name of the guest.
 * @param {string} guestEmail - The email of the guest.
 * @returns {Promise<Object>} The response from the server.
 */
export const inviteGuest = async (captainId, guestName, guestEmail, captainName, tableNumber = 1) => {
    try {
        const response = await fetch(`${API_BASE}/InviteGuest`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ captainId, guestName, guestEmail, captainName, tableNumber })
        });

        if (!response.ok) {
            const error = new Error(`Error inviting guest: ${response.statusText}`);
            error.status = response.status;
            throw error;
        }

        return await response.json();
    } catch (error) {
        console.error('Error inviting guest:', error);
        throw error;
    }
};

/**
 * Fetches the guests for a specific captain.
 * @param {string} captainId - The ID of the captain.
 * @returns {Promise<Array>} The list of guests.
 */
export const getMyGuests = async (captainId) => {
    try {
        const response = await fetch(`${API_BASE}/MyGuests`, {
            method: 'GET',
            headers: {
                'id': captainId,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Error fetching guests: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching guests:', error);
        throw error;
    }
};
/**
 * Accepts an invitation and updates guest details.
 * @param {string} userId - The ID of the guest accepting the invite.
 * @param {Object} formData - The updated guest data (name, address, meal choice).
 * @returns {Promise<Object>} The response from the server.
 */
export const acceptInvite = async (userId, formData) => {
    try {
        const response = await fetch(`${API_BASE}/AcceptInvitation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ guestId: userId, formData })
        });

        if (!response.ok) {
            throw new Error(`Error accepting invite: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error accepting invite:', error);
        throw error;
    }
};
/**
 * Checks if the invite code is valid for the guest.
 * @param {string} guestEmail - The email of the guest.
 * @param {string} inviteCode - The invitation code to check.
 * @returns {Promise<Object>} The response from the server containing validation result.
 */
export const checkInviteCode = async (guestEmail, inviteCode) => {
    try {
        const response = await fetch(`${API_BASE}/CheckInviteCode`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ guestEmail, inviteCode })
        });

        if (!response.ok) {
            const error = new Error(`Error checking invite code: ${response.statusText}`);
            error.status = response.status;
            throw error;
        }

        return await response.json();
    } catch (error) {
        console.error('Error checking invite code:', error);
        throw error;
    }
};
/**
 * Fetches guest data by ID.
 * @param {string} guestId - The ID of the guest.
 * @returns {Promise<Object>} The guest data.
 */
export const getGuestData = async (guestId) => {
    try {
        const response = await fetch(`${API_BASE}/GetGuestData?id=${guestId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Error fetching guest data: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching guest data:', error);
        throw error;
    }
};

/**
 * Declines an invitation.
 * @param {string} userId - The ID of the guest declining the invite.
 * @returns {Promise<Object>} The response from the server.
 */
export const declineInvite = async (userId) => {
    try {
        const response = await fetch(`${API_BASE}/DeclineInvitation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ guestId: userId })
        });

        if (!response.ok) {
            throw new Error(`Error declining invite: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error declining invite:', error);
        throw error;
    }
};

/**
 * Validates an address using the backend service.
 * @param {string} address - The address string to validate.
 * @returns {Promise<Object>} The validation result from the backend.
 */
export const validateAddress = async (address) => {
    try {
        const response = await fetch(`${API_BASE}/ValidateAddress`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ address })
        });

        if (!response.ok) {
            throw new Error(`Address validation failed: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error validating address:', error);
        throw error;
    }
};
/**
 * Fetches admin dashboard stats and data.
 * @returns {Promise<Object>} The dashboard data.
 */
export const getAdminDashboardData = async () => {
    try {
        const response = await fetch(`${API_BASE}/GetAdminDashboardData`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Error fetching admin dashboard data: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching admin dashboard data:', error);
        throw error;
    }
};

/**
 * Request an additional table for a captain.
 * @param {string} captainId - The ID of the captain.
 * @returns {Promise<Object>} The response from the server.
 */
export const requestAdditionalTable = async (captainId, captainName) => {
    try {
        const response = await fetch(`${API_BASE}/RequestAdditionalTable`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ captainId, captainName })
        });

        if (!response.ok) {
            throw new Error(`Error requesting additional table: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error requesting additional table:', error);
        throw error;
    }
};

/**
 * Fetch additional table requests (for admin).
 * @returns {Promise<Array>} List of requests.
 */
export const getAdditionalTableRequests = async () => {
    try {
        const response = await fetch(`${API_BASE}/GetAdditionalTableRequests`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Error fetching additional table requests: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching additional table requests:', error);
        throw error;
    }
};

/**
 * Process an additional table request (Approve or Deny).
 * @param {string} captainId - The ID of the captain to approve.
 * @param {string} decision - 'Approve' or 'Deny'
 * @returns {Promise<Object>} The response from the server.
 */
export const processAdditionalTableRequest = async (captainId, decision) => {
    try {
        const response = await fetch(`${API_BASE}/ProcessAdditionalTableRequest`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ captainId, decision })
        });

        if (!response.ok) {
            throw new Error(`Error processing additional table request: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error processing additional table request:', error);
        throw error;
    }
};

/**
 * Updates multiple guests' table numbers.
 * @param {Array<{guestId: string, tableNumber: number}>} updates - List of updates.
 * @returns {Promise<Object>} Response.
 */
export const updateGuestTables = async (updates) => {
    try {
        const response = await fetch(`${API_BASE}/UpdateGuestTables`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ updates })
        });
        if (!response.ok) throw new Error('Failed to update tables');
        return await response.json();
    } catch (error) {
        console.error('Error updating guest tables:', error);
        throw error;
    }
};

export const revokeGuest = async (guestId, captainId) => {
    try {
        const response = await fetch(`${API_BASE}/RevokeGuest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ guestId, captainId })
        });

        if (!response.ok) {
            throw new Error(`Error: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error revoking guest:', error);
        throw error;
    }
};

export const remindGuest = async (guestId) => {
    try {
        const response = await fetch(`${API_BASE}/RemindGuest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ guestId })
        });

        if (!response.ok) {
            throw new Error(`Error: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error reminding guest:', error);
        throw error;
    }
};

/**
 * Export all guests.
 * @returns {Promise<Array>} List of all guests.
 */
export const exportAllGuests = async () => {
    try {
        const response = await fetch(`${API_BASE}/ExportAllGuests`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Error exporting guests: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error exporting guests:', error);
        throw error;
    }
};

